import React, { useState } from "react";
import {
  Box,
  Typography,
  Collapse,
  IconButton,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  Divider,
  Stack,
  Autocomplete,
  Paper,
  TextField,
  CircularProgress
} from "@mui/material";
import {
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  RestartAlt as RestartAltIcon,
  Close as CloseIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Search as SearchIcon,
  Map as MapIcon,
  FilterAltOff as FilterAltOffIcon,
  ArrowForward as ArrowForwardIcon,
  East as EastIcon,
  Tune as TuneIcon,
} from "@mui/icons-material";
import { ParliamentPlot, SeatTable } from './VI_graphics';
import { isTrackingSchema } from "./utils/schemaTypes";


const PARTY_ORDER = [

        "midterms_VI_dem",
        "midterms_VI_rep"
];
const glassSx = {
  background: "rgba(255, 255, 255, 0.75)",
  backdropFilter: "blur(12px)",
  WebkitBackdropFilter: "blur(12px)",
  border: "1px solid rgba(255, 255, 255, 0.5)",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
  borderRadius: 3,
};

const GeoLabel = ({ label, loading }) => (
  <Box sx={{ position: "relative", display: "inline-flex", alignItems: "center", gap: loading ? 0.75 : 0 }}>
    {label}
    {loading && <CircularProgress size={10} thickness={5} sx={{ color: "text.disabled" }} />}
  </Box>
);

const MapToolbar = ({
  geoLevel,
  onGeoLevelChange,
  selectedStateName,
  selectedAreaLabel,
  onClearSelection,
  zoomIn,
  zoomOut,
  resetView,
  goBackOneLevel,
  poliInfo,
  hoverArea,
  selectedFeature,
  poliInfoToplevel,
  selectedDemographics,
  onClearDemographics,
  PossibleAreas,
  handleselectedFeatureChange,
  allowedLevels,
  waveSelect,
  handlewaveSelectChange,
  waveOptions,
  active_group,
  colorMap = {},
  compareWaves,
  onCompareWavesChange,
  compareKey,
  onCompareKeyChange,
  showArrows,
  onShowArrowsToggle,
  metricRows,
  geoPreloadStatus = {}
}) => {
  const [isAreaSearchOpen, setIsAreaSearchOpen] = useState(false);
  const toggleAreaSearch = () => setIsAreaSearchOpen((prev) => !prev);
  const isTracking = isTrackingSchema(active_group?.schema);
  const hasWaves = Array.isArray(waveOptions) && waveOptions.length > 0 && isTracking;
  const sortedWaves = [...(waveOptions || [])].sort((a, b) => a - b);
  const prevWave = sortedWaves[sortedWaves.length - 2];
  const latestWave = sortedWaves[sortedWaves.length - 1];
  const canCompare = sortedWaves.length >= 2;
  const isCategoricalTracking = isTracking && active_group?.type === "categorical";
  const isLikertTracking = isTracking && active_group?.type === "likert";
  const isVoteIntention = String(active_group?.label || "").toLowerCase().includes("vote intention");
  const isDontKnow = (key) => {
    const kk = String(key || "").toLowerCase();
    const lbl = String(active_group?.labels?.[key] || "").toLowerCase();
    return kk.includes("dontknow") || lbl.includes("don't know") || lbl.includes("dont know");
  };
  const displayKeys = (active_group?.keys || []).filter((k) => !(isVoteIntention && isDontKnow(k)));
  const getLabel = (key) => active_group?.labels?.[key] || key;
  // Same cascade as restyle.js getColor, so the key matches what the map paints:
  // the question's own colour dict first, then the generated palette
  // (buildLabelsAndColors), which is where categorical questions without an
  // explicit dict get their distinct colours. Without the colorMap step every
  // option in such a question fell through to one blue.
  const getColor = (key) => active_group?.colors?.[key] || colorMap?.[key] || "#2563eb";
  const compareMode = compareKey === "__top__" ? "top_pos" : compareKey === "__top_neg__" ? "top_neg" : "selected";
  const selectedPartyKey = displayKeys.includes(compareKey) ? compareKey : (displayKeys[0] || null);

  React.useEffect(() => {
    if (compareWaves && isLikertTracking && compareKey !== "net") {
      onCompareKeyChange?.("net");
    }
  }, [compareWaves, isLikertTracking, compareKey, onCompareKeyChange]);

  const calculateSeats = (rows, keys) => {
  const counts = Object.fromEntries(keys.map(k => [k, 0]));

  rows.forEach(r => {
    let maxKey = null;
    let maxVal = -Infinity;

    keys.forEach(k => {
      const v = r[k];
      if (v != null && Number.isFinite(v) && v > maxVal) {
        maxVal = v;
        maxKey = k;
      }
    });

    if (maxKey) counts[maxKey] += 1;
  });



  return counts;
};
  
const seatCounts = React.useMemo(
  () =>
    active_group?.label === "Midterm voting intention" && metricRows?.length
      ? calculateSeats(
          (isTracking && waveSelect != null
            ? metricRows.filter((row) => Number(row.wave ?? row.Wave) === Number(waveSelect))
            : metricRows
          ).filter(row => row.state !== "DC"),
          active_group.keys
        )
      : null,
  [metricRows, active_group?.label, active_group?.keys, isTracking, waveSelect]
);

  const demoPills = [];
  if (selectedDemographics) {
    Object.entries(selectedDemographics).forEach(([key, val]) => {
      const lbls = Array.isArray(val) ? val : val ? [val] : [];
      lbls.forEach((l) => demoPills.push({ key: `${key}-${l}`, label: `${key.charAt(0).toUpperCase() + key.slice(1)}: ${l}` }));
    });
  }
  const hasFilters = demoPills.length > 0;

  // Blue-teal filter colour tokens
  const teal = {
    bg: "rgba(3,105,161,0.07)",
    border: "rgba(3,105,161,0.28)",
    text: "#0c4a6e",
    pill: "rgba(3,105,161,0.1)",
    glow: "rgba(3,105,161,0.08)",
  };

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          ...glassSx,
          position: "absolute",
          top: 20,
          left: 20,
          zIndex: 1000,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          p: 0.5,
          transition: "all 0.3s ease",
          minWidth: isAreaSearchOpen ? 300 : "auto",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          
          <ToggleButtonGroup
            id="tour-geo-level"
            value={geoLevel}
            exclusive
            onChange={(e, newLevel) => {
              if (newLevel) onGeoLevelChange(newLevel);
            }}
            size="small"
            sx={{
              backgroundColor: "rgba(0,0,0,0.04)",
              borderRadius: 2,
              p: 0.5,
              "& .MuiToggleButton-root": {
                border: "none",
                borderRadius: 1.5,
                mx: 0.5,
                py: 0.5,
                px: 1.5,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.8rem",
                color: "text.secondary",
                "&.Mui-selected": {
                  backgroundColor: "#fff",
                  color: "#0f9fab",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  "&:hover": { backgroundColor: "#fff" },
                },
                "&:hover": { backgroundColor: "rgba(0,0,0,0.05)" },
              },
            }}
          >
            {allowedLevels.includes("states") && (
              <ToggleButton value="states"><GeoLabel label="States" loading={geoPreloadStatus["states"] === "loading"} /></ToggleButton>
            )}
            {allowedLevels.includes("cd") && (
              <ToggleButton value="cd"><GeoLabel label="Districts" loading={geoPreloadStatus["cd"] === "loading"} /></ToggleButton>
            )}
            {allowedLevels.includes("counties") && (
              <ToggleButton value="counties"><GeoLabel label="Counties" loading={geoPreloadStatus["counties"] === "loading"} /></ToggleButton>
            )}
            {allowedLevels.includes("statesenate") && (
              <ToggleButton value="statesenate"><GeoLabel label="State Senate" loading={geoPreloadStatus["statesenate"] === "loading"} /></ToggleButton>
            )}
            {allowedLevels.includes("statehouse") && (
              <ToggleButton value="statehouse"><GeoLabel label="State House" loading={geoPreloadStatus["statehouse"] === "loading"} /></ToggleButton>
            )}
            {allowedLevels.includes("zipcodes") && (
              <ToggleButton value="zipcodes"><GeoLabel label="Zip" loading={geoPreloadStatus["zipcodes"] === "loading"} /></ToggleButton>
            )}
            {allowedLevels.includes("media_market") && (
              <ToggleButton value="media_market"><GeoLabel label="Media Markets" loading={geoPreloadStatus["media_market"] === "loading"} /></ToggleButton>
            )}
          </ToggleButtonGroup>

          <Divider orientation="vertical" flexItem sx={{ my: 1 }} />
          <Tooltip title={isAreaSearchOpen ? "Close Search" : "Search Areas"}>
            <IconButton
              id="tour-area-search"
              size="small"
              onClick={toggleAreaSearch}
              sx={{
                width: 36,
                height: 36,
                color: isAreaSearchOpen ? "#0f9fab" : "text.secondary",
                border: "none",
                bgcolor: isAreaSearchOpen ? "#fff" : "transparent",
                boxShadow: isAreaSearchOpen ? "0 2px 8px rgba(0,0,0,0.08)" : "none",
                transition: "all 0.2s",
                "&:hover": {
                  bgcolor: isAreaSearchOpen ? "#fff" : "rgba(255,255,255,0.4)",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                },
              }}
            >
              <SearchIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>

        <Collapse in={isAreaSearchOpen} sx={{ width: "100%"}}>
           <Box sx={{ p: 1, pt: 1.5, width: 300 }}>
             <Autocomplete
              disablePortal
              size="small"
              options={PossibleAreas || []}
              getOptionLabel={(option) => option.name || ""}
              value={selectedFeature}
              onChange={(event, newValue) => {
                if (newValue === null) {
                  resetView({ LevelToResetTo: "states" });
                } else {
                  handleselectedFeatureChange({
                    level: geoLevel,
                    value: newValue?.GEOID || newValue?.area_id || newValue?.id,
                    name: newValue?.name,
                  });
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search by name..."
                  variant="standard"
                  InputProps={{
                    ...params.InputProps,
                    disableUnderline: true,
                    startAdornment: <MapIcon fontSize="small" sx={{ color: "text.disabled", mr: 1 }} />
                  }}
                  sx={{
                    bgcolor: "rgba(255,255,255,0.5)",
                    borderRadius: 3, 
                    px: 2,
                    py: 0.5,
                    boxShadow: "inset 0 1px 2px rgba(0,0,0,0.04)", 
                    "& input": { fontSize: "0.9rem" }
                  }}
                />
              )}
            />
           </Box>
        </Collapse>
      </Paper>

      {(selectedAreaLabel || hasFilters) && (
        <Box sx={{
          position: "absolute",
          top: 20,
          right: 20,
          zIndex: 1000,
          ...glassSx,
          p: 0,
          minWidth: 220,
          maxWidth: 300,
          overflow: "hidden",
          ...(hasFilters ? {
            border: `1px solid ${teal.border}`,
            boxShadow: `0 2px 12px rgba(0,0,0,0.08), 0 0 0 3px ${teal.glow}`,
          } : {}),
        }}>
          {/* Area row */}
          {selectedAreaLabel && (
            <Box sx={{ px: 1.5, py: 1, display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: "9.5px", fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.2 }}>
                  Selected area
                </Typography>
                <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#1a1a1a", lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {selectedAreaLabel}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                <Tooltip title="Go up one level" placement="bottom">
                  <span>
                    <IconButton onClick={goBackOneLevel} disabled={!geoLevel} size="small"
                      sx={{ width: 28, height: 28, borderRadius: "7px", border: "1px solid rgba(0,0,0,0.07)", bgcolor: "#f8f9fc", color: "text.secondary", "&:hover": { bgcolor: "rgba(0,0,0,0.06)" } }}>
                      <KeyboardArrowUpIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Clear selection" placement="bottom">
                  <IconButton onClick={resetView} size="small"
                    sx={{ width: 28, height: 28, borderRadius: "7px", border: "1px solid rgba(0,0,0,0.07)", bgcolor: "#f8f9fc", color: "text.secondary", "&:hover": { bgcolor: "rgba(220,38,38,0.06)", borderColor: "rgba(220,38,38,0.3)", color: "#dc2626" } }}>
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          )}

          {/* Demographics row */}
          {hasFilters && (
            <>
              {selectedAreaLabel && <Box sx={{ height: "1px", bgcolor: teal.border, mx: 1.5 }} />}
              <Box sx={{ px: 1.5, py: 0.85, bgcolor: teal.bg }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <TuneIcon sx={{ fontSize: 11, color: teal.text }} />
                    <Typography sx={{ fontSize: "9.5px", fontWeight: 700, color: teal.text, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                      Filtered view
                    </Typography>
                  </Box>
                  <Tooltip title="Clear all filters" placement="bottom">
                    <IconButton size="small" onClick={() => onClearDemographics?.()} sx={{ width: 20, height: 20, color: teal.text, opacity: 0.7, "&:hover": { opacity: 1 } }}>
                      <CloseIcon sx={{ fontSize: 12 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.4 }}>
                  {demoPills.map(({ key, label }) => (
                    <Box key={key} sx={{
                      fontSize: "10px", fontWeight: 600, color: teal.text,
                      bgcolor: teal.pill, border: `1px solid ${teal.border}`,
                      borderRadius: "5px", px: 0.75, py: 0.2, lineHeight: 1.6,
                      fontFamily: "'DM Sans', system-ui, sans-serif",
                    }}>
                      {label}
                    </Box>
                  ))}
                </Box>
              </Box>
            </>
          )}
        </Box>
      )}


      <Paper
        elevation={0}
        sx={{
          ...glassSx,
          position: "absolute",
          right: 20,
          top: "50%",
          transform: "translateY(-50%)",
          zIndex: 1000,
          display: "flex",
          flexDirection: "column",
          borderRadius: 8, 
          p: 0.5
        }}
      >
        <Tooltip title="Zoom In" placement="left">
          <IconButton onClick={zoomIn} size="medium"  sx={{ 
                width: 36, height: 36, 
                color: "text.primary", 
                border: "none", borderRadius: 8, 
                "&:hover": { bgcolor: "white" } 
            }}>
            <ZoomInIcon />
          </IconButton>
        </Tooltip>
        
        <Divider sx={{ width: "60%", mx: "auto", bgcolor: "rgba(236, 236, 236, 0.75)" }} />
        
        <Tooltip title="Zoom Out" placement="left">
          <IconButton onClick={zoomOut} size="medium"  sx={{ 
                width: 36, height: 36, 
                color: "text.primary", 
                border: "none", 
                "&:hover": { bgcolor: "white" } 
            }}>
            <ZoomOutIcon />
          </IconButton>
        </Tooltip>
        
        <Divider sx={{ width: "60%", mx: "auto", bgcolor: "rgba(225, 225, 225, 0.91)" }} />
        
        <Tooltip title="Reset View" placement="left">
          <IconButton onClick={resetView} size="medium" sx={{ 
                width: 36, height: 36, 
                color: "text.primary", 
                border: "none", 
                "&:hover": { bgcolor: "white" } 
            }}>
            <RestartAltIcon />
          </IconButton>
        </Tooltip>
      </Paper>

      {hasWaves && (
  <Paper
    id="tour-wave-select"
    elevation={0}
    sx={{
      ...glassSx,
      position: "absolute",
      bottom: 24,
      right: 20,
      zIndex: 1000,
      overflow: "hidden",
      width: 248,
    }}
  >
    {!compareWaves ? (
      <Box sx={{ px: 1.5, pt: 1.25, pb: 1.25 }}>
        <Typography sx={{ fontSize: "9.5px", fontWeight: 700, color: "text.disabled", textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.75 }}>
          Wave
        </Typography>
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mb: 1.25 }}>
          {sortedWaves.map((wave) => (
            <Box
              key={wave}
              onClick={() => handlewaveSelectChange?.(wave)}
              sx={{
                px: 1.25,
                py: 0.45,
                borderRadius: "7px",
                border: "1px solid",
                borderColor: waveSelect === wave ? "primary.main" : "divider",
                bgcolor: waveSelect === wave ? "rgba(37,99,235,0.08)" : "background.paper",
                color: waveSelect === wave ? "primary.main" : "text.secondary",
                fontSize: "12px",
                fontWeight: waveSelect === wave ? 700 : 500,
                cursor: "pointer",
                transition: "all 0.12s",
                "&:hover": { borderColor: "primary.main", color: "primary.main" },
              }}
            >
              {wave}
            </Box>
          ))}
        </Box>
        {canCompare && (
          <Box
            onClick={() => onCompareWavesChange?.(true)}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.75,
              px: 1,
              py: 0.7,
              borderRadius: "8px",
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
              cursor: "pointer",
              transition: "all 0.12s",
              "&:hover": { borderColor: "primary.main", bgcolor: "rgba(37,99,235,0.05)" },
            }}
          >
            <EastIcon sx={{ fontSize: 13, color: "text.disabled" }} />
            <Typography sx={{ fontSize: "11.5px", fontWeight: 600, color: "text.secondary" }}>
              Compare waves
            </Typography>
          </Box>
        )}
      </Box>
    ) : (
      <>
        <Box sx={{ px: 1.5, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", bgcolor: "rgba(37,99,235,0.04)", borderBottom: "1px solid", borderColor: "divider" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
              <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "text.secondary" }}>
                Wave {prevWave}
              </Typography>
              <EastIcon sx={{ fontSize: 12, color: "primary.main" }} />
              <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "primary.main" }}>
                Wave {latestWave}
              </Typography>
            </Box>
            <Tooltip title={showArrows !== false ? "Hide movement arrows" : "Show movement arrows"} placement="top">
              <Box
                onClick={() => onShowArrowsToggle?.()}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 26,
                  height: 26,
                  borderRadius: "6px",
                  border: "1px solid",
                  borderColor: showArrows !== false ? "primary.main" : "divider",
                  bgcolor: showArrows !== false ? "rgba(37,99,235,0.08)" : "transparent",
                  color: showArrows !== false ? "primary.main" : "text.disabled",
                  cursor: "pointer",
                  transition: "all 0.12s",
                  "&:hover": { borderColor: "primary.main", color: "primary.main" },
                }}
              >
                <ArrowForwardIcon sx={{ fontSize: 12, transform: "rotate(-45deg)" }} />
              </Box>
            </Tooltip>
          </Box>
          <Tooltip title="Exit compare" placement="top">
            <IconButton size="small" onClick={() => onCompareWavesChange?.(false)} sx={{ width: 24, height: 24, color: "text.disabled", "&:hover": { color: "#dc2626", bgcolor: "rgba(220,38,38,0.06)" } }}>
              <CloseIcon sx={{ fontSize: 13 }} />
            </IconButton>
          </Tooltip>
        </Box>
        {(isCategoricalTracking || isLikertTracking || displayKeys.length > 1) && (
          <Box sx={{ px: 1.5, py: 1.25 }}>
            {isCategoricalTracking && (
              <>
                <Typography sx={{ fontSize: "9.5px", fontWeight: 700, color: "text.disabled", textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.75 }}>
                  Map mode
                </Typography>
                <Box sx={{ display: "flex", gap: 0.5, mb: 1.25 }}>
                  {[
                    { val: "top_pos", label: "Biggest winner" },
                    { val: "top_neg", label: "Biggest loser" },
                    { val: "selected", label: "By category" },
                  ].map(({ val, label }) => (
                    <Box
                      key={val}
                      onClick={() => {
                        if (val === "top_pos") onCompareKeyChange?.("__top__");
                        else if (val === "top_neg") onCompareKeyChange?.("__top_neg__");
                        else onCompareKeyChange?.(selectedPartyKey || "__top__");
                      }}
                      sx={{
                        flex: 1,
                        textAlign: "center",
                        px: 0.5,
                        py: 0.55,
                        borderRadius: "7px",
                        border: "1px solid",
                        borderColor: compareMode === val ? "primary.main" : "divider",
                        bgcolor: compareMode === val ? "rgba(37,99,235,0.08)" : "background.paper",
                        color: compareMode === val ? "primary.main" : "text.secondary",
                        fontSize: "10.5px",
                        fontWeight: compareMode === val ? 700 : 500,
                        cursor: "pointer",
                        lineHeight: 1.3,
                        transition: "all 0.12s",
                        "&:hover": { borderColor: "primary.main", color: "primary.main" },
                      }}
                    >
                      {label}
                    </Box>
                  ))}
                </Box>
              </>
            )}

            {(isCategoricalTracking && compareMode === "selected") && (
              <>
                <Typography sx={{ fontSize: "9.5px", fontWeight: 700, color: "text.disabled", textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.65 }}>
                  Category
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.45 }}>
                  {displayKeys.map((key) => {
                    const col = getColor(key);
                    const lbl = getLabel(key);
                    const isActive = (compareKey === key) || (!displayKeys.includes(compareKey) && key === selectedPartyKey);
                    return (
                      <Box
                        key={key}
                        onClick={() => onCompareKeyChange?.(key)}
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.5,
                          px: 0.85,
                          py: 0.35,
                          borderRadius: "6px",
                          border: "1px solid",
                          borderColor: isActive ? col : "divider",
                          bgcolor: isActive ? `${col}18` : "transparent",
                          color: isActive ? col : "text.secondary",
                          fontSize: "10.5px",
                          fontWeight: isActive ? 700 : 500,
                          cursor: "pointer",
                          transition: "all 0.12s",
                          "&:hover": { borderColor: col, color: col },
                        }}
                      >
                        <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: col, flexShrink: 0 }} />
                        {lbl}
                      </Box>
                    );
                  })}
                </Box>
              </>
            )}

            {isLikertTracking && (
              <Box sx={{ mb: 1 }}>
                <Typography sx={{ fontSize: "9.5px", fontWeight: 700, color: "text.disabled", textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.4 }}>
                  Compare mode
                </Typography>
                <Typography sx={{ fontSize: "10.5px", color: "text.secondary", lineHeight: 1.4 }}>
                  Net change
                </Typography>
              </Box>
            )}

            <Box sx={{ pt: 1, borderTop: "1px solid", borderColor: "divider", mt: compareMode === "selected" ? 1 : 0 }}>
              <Typography sx={{ fontSize: "9.5px", fontWeight: 700, color: "text.disabled", textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.6 }}>
                Map key
              </Typography>
              {!isCategoricalTracking || compareMode === "selected" ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.4 }}>
                  {[
                    { color: "#15803d", label: "Gaining support" },
                    { color: "#e5e7eb", label: "Stable / no change" },
                    { color: "#b91c1c", label: "Losing support" },
                  ].map(({ color, label }) => (
                    <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      <Box sx={{ width: 10, height: 6, borderRadius: "2px", bgcolor: color, flexShrink: 0 }} />
                      <Typography sx={{ fontSize: "10.5px", color: "text.secondary" }}>
                        {label}
                      </Typography>
                    </Box>
                  ))}
                  <Box sx={{ mt: 0.4, display: "flex", gap: 1.5 }}>
                    {[{ color: "#16a34a", label: "↑ Gaining" }, { color: "#dc2626", label: "↓ Losing" }].map(({ color, label }) => (
                      <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: "9.5px", color: "text.disabled" }}>{label}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.4 }}>
                  {displayKeys.map((key) => {
                    const col = getColor(key);
                    if (!col) return null;
                    return (
                      <Box key={key} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <Box sx={{ width: 10, height: 6, borderRadius: "2px", bgcolor: col, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: "10.5px", color: "text.secondary" }}>
                          {getLabel(key)}
                        </Typography>
                      </Box>
                    );
                  })}
                  <Typography sx={{ mt: 0.25, fontSize: "9.5px", color: "text.disabled", fontStyle: "italic" }}>
                    Shade = movement strength
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </>
    )}
  </Paper>
)}


      {active_group &&metricRows && active_group?.label === "Midterm voting intention" && geoLevel==="cd" &&(
  <>
            <Box
            sx={{
              position: "absolute",
              bottom: 20,
              left: 16,
              zIndex: 2,
              backgroundColor: "rgba(255,255,255,0.95)",
              borderRadius: 2,
              px: 2,
              py: 1.5,
              pointerEvents: "auto",
              boxShadow: 2,
              minWidth: 100, 
            }}>
    <div style={{TextAlign: "center" }}>
      <ParliamentPlot
  seatCounts={seatCounts}
  colors={active_group.colors}
  rows={15}
  dotRadius={1.9}
  partyOrder={PARTY_ORDER} />
    </div>
    <SeatTable
  seatCounts={seatCounts}
  labels={active_group.labels}
  colors={active_group.colors}
/>
      </Box>
  </>
)}
    </>
    
  );
};

export default MapToolbar;
